/** Title + icon for a tab URL (from the cached workspace). */
import { Shell, Sidebar, type DocsCache, type WorkspaceCache } from "@bundle/core";
import type { Translate } from "@bundle/ui";
import type { IconName } from "@bundle/tokens";
import type { PageNode } from "@bundle/contracts";

/** `emoji`: a page's own icon (content) shown instead of `icon`. */
export type TabInfo = { icon: IconName; title: string; avatar?: { name: string; square: boolean }; emoji?: string };

function pageTab(docs: DocsCache | undefined, known: Record<string, PageNode> | undefined, id: string, fallback: IconName, t: Translate): TabInfo {
  const p = docs?.pages[id] ?? known?.[id];
  if (p === undefined) return { icon: fallback, title: t("page.untitled") };
  const info: TabInfo = { icon: p.kind === "database" ? "board" : "page", title: p.title === "" ? t("page.untitled") : p.title };
  if (p.icon !== undefined && !p.icon.startsWith("icon:")) info.emoji = p.icon;
  return info;
}

/** `known`: pages opened but not in the tree (database rows). */
export function tabInfo(url: string, cache: WorkspaceCache | undefined, t: Translate, docs?: DocsCache, known?: Record<string, PageNode>): TabInfo {
  const r = Shell.parseUrl(url);
  if (r === undefined) return { icon: "globe", title: url };
  switch (r.type) {
    case "home":
      return { icon: "home", title: t("nav.home") };
    case "channel": {
      const c = cache?.channels[r.channelId];
      if (c === undefined) return { icon: "hash", title: r.channelId };
      if (c.kind === "dm" || c.kind === "group_dm") {
        const title = Sidebar.channelTitle(cache!, c.id);
        const other = c.dmUserIds?.find((u) => u !== cache!.meId);
        const m = other === undefined ? undefined : cache!.members[other];
        return { icon: "message", title, avatar: { name: m?.displayName ?? title, square: m?.kind === "agent" || c.kind === "group_dm" } };
      }
      return { icon: c.kind === "private" ? "lock" : "hash", title: c.name };
    }
    case "dm": {
      const m = cache?.members[r.userId];
      return { icon: "message", title: m?.displayName ?? r.userId, avatar: { name: m?.displayName ?? "?", square: m?.kind === "agent" } };
    }
    case "thread":
      return { icon: "thread", title: t("thread.title") };
    case "page":
      return pageTab(docs, known, r.pageId, "page", t);
    case "db":
      return pageTab(docs, known, r.dbId, "board", t);
    case "projects":
      return { icon: "board", title: t("nav.projects") };
    case "search":
      return { icon: "search", title: r.query === "" ? t("nav.search") : r.query };
    case "activity":
      return { icon: "bell", title: t("nav.activity") };
    case "later":
      return { icon: "bookmark", title: t("nav.later") };
    case "mail":
      return { icon: "mail", title: t("nav.mail") };
    case "mailThread":
      return { icon: "mail", title: t("mail.title") };
    case "contacts":
      return { icon: "contacts", title: t("nav.contacts") };
    case "docs":
      return { icon: "page", title: t("nav.docs") };
    case "calendar":
      return { icon: "calendar", title: t("nav.calendar") };
    case "unreads":
      return { icon: "inbox", title: t("nav.unreads") };
    case "scheduled":
      return { icon: "send", title: t("nav.scheduled") };
    case "drafts":
      return { icon: "edit", title: t("nav.drafts") };
    case "agents":
      return { icon: "agent", title: t("nav.agents") };
    case "huddles":
      return { icon: "huddle", title: t("nav.huddles") };
    case "files":
      return { icon: "folder", title: t("drive.title") };
    case "huddle": {
      // The route id is the conversation the huddle belongs to, or `meeting:<code>` for a link's huddle.
      if (r.huddleId.startsWith("meeting:")) return { icon: "huddle", title: cache?.huddles?.[r.huddleId]?.meeting?.title ?? t("huddle.title") };
      const c = cache?.channels[r.huddleId];
      if (c === undefined) return { icon: "huddle", title: t("huddle.title") };
      const name = Sidebar.channelTitle(cache!, c.id);
      return { icon: "huddle", title: c.kind === "dm" || c.kind === "group_dm" ? name : `# ${name}` };
    }
    case "file":
      return { icon: "file", title: t("files.page.title") };
    case "settings":
      return { icon: "settings", title: t("settings.title") };
  }
}

/**
 * The browser tab / window title, in Slack's format: "general (Channel) - Acme - Bundle", "Ada (DM) - Acme - Bundle",
 * "Activity - Acme - Bundle"; "! " in front while I have unread mentions (DMs count), else "* " while anything is
 * unread (unmuted conversations).
 */
export function documentTitle(url: string, cache: WorkspaceCache | undefined, title: string, t: Translate, unread: { mentions: number; any: boolean }): string {
  const r = Shell.parseUrl(url);
  let label = title;
  if (r?.type === "dm") label = t("tabTitle.dm", { name: title });
  else if (r?.type === "channel") {
    const kind = cache?.channels[r.channelId]?.kind;
    label = kind === "dm" || kind === "group_dm" ? t("tabTitle.dm", { name: title }) : t("tabTitle.channel", { name: title });
  }
  const prefix = unread.mentions > 0 ? "! " : unread.any ? "* " : "";
  const ws = cache?.workspace.name;
  return `${prefix}${label}${ws === undefined || ws === "" ? "" : ` - ${ws}`} - Bundle`;
}
